/**
 * The sheets behind decisions: Reddet asks for a reason, Düzelt edits the English text,
 * request actions ask for a date or a reason. None of them acts externally; approvals
 * still go through the executor path with its undo window.
 */

import { REJECT_REASON_CODES, REQUEST_REJECT_CODES } from '@twin/contracts';
import { createContext, type ReactNode, useContext, useState } from 'react';
import { evaluateChange, MIN_REASON_LENGTH, reasonText } from '../api/policy-rules.js';
import type { PolicyChange, Settings } from '../api/settings.js';
import {
  REJECT_REASON_LABELS,
  REQUEST_REJECT_REASON_LABELS,
  type RejectReasonCode,
  type RequestRejectCode,
} from '../api/types.js';
import { usePanel, useStore } from '../app/context.js';
import { canEdit } from '../app/selectors.js';
import { addIsoDays, formatIsoDay, istanbulIsoDate } from '../lib/format.js';
import { POLICY_HOLD_MS, T4HoldButton } from './HoldButton.js';
import { Icon } from './icons.js';
import { ChoiceChips, Sheet } from './Sheet.js';

export type SheetRequest =
  | { kind: 'reject'; proposalId: string; onDone?: () => void }
  | { kind: 'edit'; proposalId: string; onApproved?: () => void }
  | { kind: 'answer'; requestId: string; prefill?: string }
  | { kind: 'accept'; requestId: string }
  | { kind: 'defer'; requestId: string }
  | { kind: 'request-reject'; requestId: string }
  | { kind: 'loosen'; change: PolicyChange };

interface SheetApi {
  open: (sheet: SheetRequest) => void;
  close: () => void;
  current: SheetRequest | null;
}

const SheetContext = createContext<SheetApi | null>(null);

export function useSheets(): SheetApi {
  const api = useContext(SheetContext);
  if (!api) throw new Error('SheetProvider is missing');
  return api;
}

export function SheetProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<SheetRequest | null>(null);
  const api: SheetApi = { open: setCurrent, close: () => setCurrent(null), current };
  return (
    <SheetContext.Provider value={api}>
      {children}
      <SheetHost />
    </SheetContext.Provider>
  );
}

/** Reason codes as the API takes them; the chips show REJECT_REASON_LABELS (2.10 row 5). */
export const REJECT_REASONS: readonly RejectReasonCode[] = REJECT_REASON_CODES;
export const REQUEST_REJECT_REASONS: readonly RequestRejectCode[] = REQUEST_REJECT_CODES;
export const DEFER_OPTIONS = ['Yarın', '3 gün', '1 hafta'] as const;
/** Days from today (Europe/Istanbul) of each defer choice. */
export const DEFER_DAYS: Readonly<Record<(typeof DEFER_OPTIONS)[number], number>> = {
  Yarın: 1,
  '3 gün': 3,
  '1 hafta': 7,
};
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function RejectSheet({ proposalId, onDone }: { proposalId: string; onDone?: (() => void) | undefined }) {
  const store = useStore();
  const { close } = useSheets();
  const [reason, setReason] = useState<RejectReasonCode | null>(null);
  const [note, setNote] = useState('');
  return (
    <Sheet
      title="Neden reddediyorsun?"
      description="Gerekçe ajana gider ve çalıştırma kaydına yazılır."
      onClose={close}
      actions={
        <>
          <button type="button" className="btn btn--secondary" onClick={close}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn btn--danger-outline"
            disabled={reason === null}
            onClick={() => {
              if (!reason) return;
              close();
              void store.reject(proposalId, reason, note.trim()).then((ok) => {
                if (ok) onDone?.();
              });
            }}
          >
            <Icon name="x" size={16} />
            Reddet
          </button>
        </>
      }
    >
      <ChoiceChips
        label="Ret gerekçesi"
        options={REJECT_REASONS}
        labels={REJECT_REASON_LABELS}
        value={reason}
        onChange={setReason}
      />
      <label className="field">
        <span className="field__label">Not (isteğe bağlı)</span>
        <textarea className="field__input" rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
    </Sheet>
  );
}

function EditSheet({ proposalId, onApproved }: { proposalId: string; onApproved?: (() => void) | undefined }) {
  const store = useStore();
  const state = usePanel();
  const { close } = useSheets();
  const proposal = state.snapshot?.proposals.find((item) => item.id === proposalId);
  const initial =
    proposal?.kind === 'customer_reply'
      ? proposal.draft.en
      : proposal?.kind === 'listing_title'
        ? proposal.diff.after
        : '';
  const [text, setText] = useState(initial);
  // Düzelt approves in the same step, so it never exists for an item that needs the hold.
  if (!proposal || !canEdit(proposal)) return null;
  const blocker = store.approveBlocker(proposalId);
  const listing = proposal.kind === 'listing_title';
  return (
    <Sheet
      title={listing ? 'Başlığı düzelt' : 'Cevabı düzelt'}
      description="Düzelttiğin metin aynı yürütücü yolundan geçer; kayda “Sen düzelttin” yazılır."
      onClose={close}
      actions={
        <>
          <button type="button" className="btn btn--secondary" onClick={close}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={blocker !== null || text.trim() === ''}
            onClick={() => {
              const edited = text.trim();
              // Start the copy inside the click; the approval waits until the text is on the clipboard.
              const copying = proposal.executor.kind === 'manual_copy' ? copyText(edited) : null;
              close();
              void (async () => {
                const copied = copying ? await copying : undefined;
                const ticket = await store.approve(proposalId, {
                  editedText: edited,
                  ...(copied === undefined ? {} : { copied }),
                });
                if (ticket) onApproved?.();
              })();
            }}
          >
            <Icon name="check" size={16} stroke={2} />
            {blocker === 'offline' ? 'Bağlantı bekleniyor' : 'Düzelt ve onayla'}
          </button>
        </>
      }
    >
      <label className="field">
        <span className="field__label key">{listing ? 'Sonra · Etsy ilanına gider' : 'EN · müşteriye gider'}</span>
        <textarea
          className="field__input field__input--en"
          lang="en"
          rows={6}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
      </label>
    </Sheet>
  );
}

/** Copies text inside the click handler, while the user activation is still valid. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function AnswerSheet({ requestId, prefill = '' }: { requestId: string; prefill?: string }) {
  const store = useStore();
  const state = usePanel();
  const { close } = useSheets();
  const request = state.snapshot?.requests.find((item) => item.id === requestId);
  const [text, setText] = useState(prefill);
  const [addToKb, setAddToKb] = useState(true);
  if (!request) return null;
  return (
    <Sheet
      title="Cevapla"
      description={request.title}
      onClose={close}
      actions={
        <>
          <button type="button" className="btn btn--secondary" onClick={close}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={text.trim() === ''}
            onClick={() => {
              close();
              void store.answerRequest(requestId, text.trim(), addToKb);
            }}
          >
            Cevabı kaydet
          </button>
        </>
      }
    >
      {request.type === 'question' ? (
        <>
          {request.question.tr !== null && request.question.tr !== request.question.en ? (
            <p lang="en" className="sheet__after">
              {request.question.en}
            </p>
          ) : null}
          {request.options.length > 0 ? (
            <fieldset className="qoptions">
              <legend className="sr-only">Hazır cevaplar</legend>
              {request.options.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={text === option ? 'qoption qoption--on' : 'qoption'}
                  onClick={() => setText(option)}
                >
                  {option}
                </button>
              ))}
            </fieldset>
          ) : null}
        </>
      ) : null}
      <label className="field">
        <span className="field__label">Cevabın (Türkçe yazabilirsin)</span>
        <textarea className="field__input" rows={4} value={text} onChange={(event) => setText(event.target.value)} />
      </label>
      <label className="check">
        <input type="checkbox" checked={addToKb} onChange={(event) => setAddToKb(event.target.checked)} />
        Bilgi bankasına ekle
      </label>
      {request.type === 'question' ? (
        <p className="sheet__after">
          <span className="key ink-2">Cevaplayınca</span> {request.afterAnswer}
        </p>
      ) : null}
    </Sheet>
  );
}

function AcceptSheet({ requestId }: { requestId: string }) {
  const store = useStore();
  const state = usePanel();
  const { close } = useSheets();
  const request = state.snapshot?.requests.find((item) => item.id === requestId);
  const today = istanbulIsoDate(state.now);
  // A design request suggests its target day; the API takes a calendar day (IsoDate).
  const suggested = request?.type === 'design' && request.dueDate !== null ? request.dueDate : '';
  const [date, setDate] = useState(suggested);
  const [note, setNote] = useState('');
  if (!request) return null;
  const valid = DATE_PATTERN.test(date) && date >= today;
  return (
    <Sheet
      title="Kabul et"
      description={`${request.title} · ekip teslimi hedef güne kadar bekler.`}
      onClose={close}
      actions={
        <>
          <button type="button" className="btn btn--secondary" onClick={close}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!valid}
            onClick={() => {
              close();
              void store.acceptRequest(requestId, date, note.trim());
            }}
          >
            <Icon name="calendar" size={16} />
            Kabul et
          </button>
        </>
      }
    >
      <label className="field">
        <span className="field__label">Hedef tarih{date ? ` · ${formatIsoDay(date)}` : ''}</span>
        <input
          className="field__input"
          type="date"
          min={today}
          value={date}
          onChange={(event) => setDate(event.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">Ekibe not (isteğe bağlı)</span>
        <textarea className="field__input" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
    </Sheet>
  );
}

function DeferSheet({ requestId }: { requestId: string }) {
  const store = useStore();
  const state = usePanel();
  const { close } = useSheets();
  const [choice, setChoice] = useState<(typeof DEFER_OPTIONS)[number] | 'Tarih seç' | null>(null);
  const [date, setDate] = useState('');
  // Choices are Europe/Istanbul days from today; the API stores 09:00 of that day.
  const today = istanbulIsoDate(state.now);
  const picked = DATE_PATTERN.test(date) && date > today ? date : null;
  const until = choice === null ? null : choice === 'Tarih seç' ? picked : addIsoDays(today, DEFER_DAYS[choice]);
  return (
    <Sheet
      title="Ertele"
      {...(until ? { description: `${formatIsoDay(until)} sabahı yeniden sorulur` } : {})}
      onClose={close}
      actions={
        <>
          <button type="button" className="btn btn--secondary" onClick={close}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!until}
            onClick={() => {
              if (!until) return;
              close();
              void store.deferRequest(requestId, until);
            }}
          >
            <Icon name="clock" size={16} />
            Ertele
          </button>
        </>
      }
    >
      <ChoiceChips
        label="Ne kadar ertelensin"
        options={[...DEFER_OPTIONS, 'Tarih seç'] as const}
        value={choice}
        onChange={setChoice}
      />
      {choice === 'Tarih seç' ? (
        <label className="field">
          <span className="field__label">Tarih</span>
          <input
            className="field__input"
            type="date"
            min={addIsoDays(today, 1)}
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
      ) : null}
    </Sheet>
  );
}

function RequestRejectSheet({ requestId }: { requestId: string }) {
  const store = useStore();
  const { close } = useSheets();
  const [reason, setReason] = useState<RequestRejectCode | null>(null);
  const [note, setNote] = useState('');
  return (
    <Sheet
      title="Neden reddediyorsun?"
      onClose={close}
      actions={
        <>
          <button type="button" className="btn btn--secondary" onClick={close}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn btn--danger-outline"
            disabled={reason === null}
            onClick={() => {
              if (!reason) return;
              close();
              void store.rejectRequest(requestId, reason, note.trim());
            }}
          >
            <Icon name="x" size={16} />
            Reddet
          </button>
        </>
      }
    >
      <ChoiceChips
        label="Ret gerekçesi"
        options={REQUEST_REJECT_REASONS}
        labels={REQUEST_REJECT_REASON_LABELS}
        value={reason}
        onChange={setReason}
      />
      <label className="field">
        <span className="field__label">Not (isteğe bağlı)</span>
        <textarea className="field__input" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
    </Sheet>
  );
}

/** What loosening this setting does, one sentence from the policy itself. */
function loosenEffect(settings: Settings, change: PolicyChange): string {
  switch (change.kind) {
    case 'number':
      return settings.policies.find((item) => item.id === change.id)?.effect ?? '';
    case 'flag':
      return settings.flags.find((item) => item.id === change.id)?.effect ?? '';
    case 'stage': {
      const option = settings.stage.stages.find((item) => item.value === change.value);
      return option ? `Aşama ${option.value}: ${option.label}.` : '';
    }
    case 'agent': {
      const agent = settings.agents.find((item) => item.id === change.id);
      return agent ? `${agent.name} yeniden çalışmaya açılır; bağlantısı yoksa yine bekler.` : '';
    }
  }
}

/**
 * Loosening a limit: the change, what it does, a required reason and a 1.5 s hold. The
 * reason goes to the change log with the audit entry. Closed while the data is stale.
 */
function LoosenSheet({ change }: { change: PolicyChange }) {
  const store = useStore();
  const state = usePanel();
  const { close } = useSheets();
  const [reason, setReason] = useState('');
  const settings = state.settings;
  if (!settings) return null;
  const evaluation = evaluateChange(settings, change);
  const reasonOk = reasonText(reason).length >= MIN_REASON_LENGTH;
  const disabledReason = evaluation.violation
    ? 'Bu değişiklik yapılamaz'
    : !store.canLoosen()
      ? 'Bağlantı bekleniyor'
      : reasonOk
        ? null
        : 'Önce gerekçe yaz';
  return (
    <Sheet
      title={`Gevşet: ${evaluation.label}`}
      description="Gevşetmek gerekçesiyle değişiklik kaydına yazılır. Sıkılaştırmak tek dokunuştur."
      onClose={close}
      actions={
        <>
          <button type="button" className="btn btn--secondary loosen__cancel" onClick={close}>
            Vazgeç
          </button>
          <T4HoldButton
            compact
            label={`${evaluation.to} yap`}
            sublabel="1,5 sn basılı tut"
            holdVerb="gevşetmek"
            durationMs={POLICY_HOLD_MS}
            disabledReason={disabledReason}
            onComplete={() => {
              const text = reasonText(reason);
              close();
              void store.changePolicy(change, { reason: text, confirmedByHold: true });
            }}
            onEarlyRelease={() => store.showToast('Gevşetmek için 1,5 sn basılı tut', { icon: 'info' })}
          />
        </>
      }
    >
      <dl className="loosen__change">
        <div>
          <dt className="key ink-2">Şimdi</dt>
          <dd className="mono">{evaluation.from}</dd>
        </div>
        <Icon name="arrow-right" size={16} className="ink-3" />
        <div>
          <dt className="key ink-2">Yeni</dt>
          <dd className="mono loosen__to">{evaluation.to}</dd>
        </div>
      </dl>
      {evaluation.violation ? (
        <p className="loosen__blocked" role="alert">
          {evaluation.violation.message}
        </p>
      ) : (
        <p className="loosen__effect">{loosenEffect(settings, change)}</p>
      )}
      <label className="field">
        <span className="field__label">Gerekçe · zorunlu, değişiklik kaydına yazılır</span>
        <textarea
          className="field__input"
          rows={3}
          value={reason}
          aria-required="true"
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
    </Sheet>
  );
}

/** Asked when Sercan comes back after copying a customer reply to Etsy. */
function SentSheet() {
  const store = useStore();
  const { awaitingSend } = usePanel();
  const first = awaitingSend[0];
  if (!first) return null;
  return (
    <Sheet
      title={first.copy ? "Cevabı Etsy'de gönderdin mi?" : 'Elle yapılacak işi yaptın mı?'}
      description={first.title}
      onClose={() => store.dismissSentPrompt()}
      actions={
        <>
          <button type="button" className="btn btn--secondary" onClick={() => store.dismissSentPrompt()}>
            Henüz değil
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void store.markSent(first.proposalId)}>
            {first.copy ? 'Gönderdim' : 'Yaptım'}
          </button>
        </>
      }
    />
  );
}

function SheetHost() {
  const { current } = useSheets();
  const { askSent } = usePanel();
  if (current) {
    switch (current.kind) {
      case 'reject':
        return <RejectSheet proposalId={current.proposalId} onDone={current.onDone} />;
      case 'edit':
        return <EditSheet proposalId={current.proposalId} onApproved={current.onApproved} />;
      case 'answer':
        return (
          <AnswerSheet
            requestId={current.requestId}
            {...(current.prefill === undefined ? {} : { prefill: current.prefill })}
          />
        );
      case 'accept':
        return <AcceptSheet requestId={current.requestId} />;
      case 'defer':
        return <DeferSheet requestId={current.requestId} />;
      case 'request-reject':
        return <RequestRejectSheet requestId={current.requestId} />;
      case 'loosen':
        return <LoosenSheet change={current.change} />;
    }
  }
  return askSent ? <SentSheet /> : null;
}
